import React, { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/shallow';
import { useEditorStore } from '../../../stores';
import { useAxesAlignmentStore } from '../../../stores';
import useTrimlineStore from '../../../stores/useTrimlineStore';
import * as THREE from 'three';
import FineTuneControls from './FineTuneControls';
import { AxisColors, ThreeAxisColors } from '../../../constants/axisConfig';
import changeCameraDirection, { CameraDirection } from '../../../utils/CameraTransformValues';

/**
 * Transform trimline points and bezier handles to match the transformed geometry
 * This is critical to ensure trimline coordinates stay synchronized with mesh after alignment
 * @param {THREE.Matrix4} transformMatrix - The transformation matrix applied to the geometry
 */
function transformTrimlineData(transformMatrix) {
  const trimlineStore = useTrimlineStore.getState();
  const { trimline, setTrimline } = trimlineStore;

  if (!trimline || !trimline.points || trimline.points.length === 0) {
    console.log('⚠️ No trimline data to transform');
    return;
  }

  console.log('🔄 Transforming trimline data with alignment matrix...');

  // Transform all trimline points
  const transformedPoints = trimline.points.map(point => {
    const vec = new THREE.Vector3(point.x, point.y, point.z);
    vec.applyMatrix4(transformMatrix);
    return { x: vec.x, y: vec.y, z: vec.z };
  });

  // Transform all bezier handles
  // Note: Handles are relative offsets, so we only apply rotation/scale, not translation
  const transformedHandles = trimline.bezierHandles.map(handle => {
    const inVec = new THREE.Vector3(handle.in.x, handle.in.y, handle.in.z);
    const outVec = new THREE.Vector3(handle.out.x, handle.out.y, handle.out.z);

    // Apply transformation but ignore translation component
    // Create a copy of the matrix with translation zeroed out
    const rotationScaleMatrix = transformMatrix.clone();
    rotationScaleMatrix.setPosition(0, 0, 0);

    inVec.applyMatrix4(rotationScaleMatrix);
    outVec.applyMatrix4(rotationScaleMatrix);

    return {
      in: { x: inVec.x, y: inVec.y, z: inVec.z },
      out: { x: outVec.x, y: outVec.y, z: outVec.z }
    };
  });

  // Update trimline store with transformed data
  setTrimline({
    ...trimline,
    points: transformedPoints,
    bezierHandles: transformedHandles
  });

  // Also transform initial points if they exist (for reset functionality)
  if (trimlineStore.initialPoints && trimlineStore.initialPoints.length > 0) {
    const transformedInitialPoints = trimlineStore.initialPoints.map(point => {
      const vec = new THREE.Vector3(point.x, point.y, point.z);
      vec.applyMatrix4(transformMatrix);
      return { x: vec.x, y: vec.y, z: vec.z };
    });

    const transformedInitialHandles = trimlineStore.initialBezierHandles?.map(handle => {
      const inVec = new THREE.Vector3(handle.in.x, handle.in.y, handle.in.z);
      const outVec = new THREE.Vector3(handle.out.x, handle.out.y, handle.out.z);

      const rotationScaleMatrix = transformMatrix.clone();
      rotationScaleMatrix.setPosition(0, 0, 0);

      inVec.applyMatrix4(rotationScaleMatrix);
      outVec.applyMatrix4(rotationScaleMatrix);

      return {
        in: { x: inVec.x, y: inVec.y, z: inVec.z },
        out: { x: outVec.x, y: outVec.y, z: outVec.z }
      };
    });

    trimlineStore.setInitialPoints(transformedInitialPoints, transformedInitialHandles);
  }

  console.log('✅ Trimline data transformed successfully');
}

const AlignmentHandler = () => {
  const scene = useEditorStore(s => s.sceneRefs.scene);
  const renderer = useEditorStore(s => s.sceneRefs.renderer);
  const shadedMesh = useEditorStore(s => s.sceneRefs.shadedMesh);

    const [showLasers, currentView] = useEditorStore(
        useShallow((state) => [
        state.toolSettings.scan_axes_movement?.showLasers || false,
        state.toolSettings.scan_axes_movement?.currentView || 'face',   //face, top, external
        ])
    );

    const viewToDirection = {
        face: CameraDirection.FRONT,
        top: CameraDirection.UPPER,
        external: CameraDirection.MID,
    }

  const { selections, isAligned, setAlignmentStatus } = useAxesAlignmentStore(
    useShallow(s => ({
      selections: s.selections,
      isAligned: s.isAligned,
      setAlignmentStatus: s.setAlignmentStatus,
    }))
  );

  const worldAxesRef = useRef();
  const originRef = useRef();

  // Effect to manage and display scene axes helper
  useEffect(() => {
    if (!scene) return;
    if (!worldAxesRef.current) {

      const fullAxes = createFullAxes(50, {x : ThreeAxisColors.X, y : ThreeAxisColors.Y, z : ThreeAxisColors.Z});
      fullAxes.traverse(obj => {
        if (obj.material) {
            obj.material.depthTest = false;  // Ignore depth buffer
            obj.renderOrder = 999;           // Render last
        }
    });
      fullAxes.position.set(0, 0, 0); // Set the origin of the axes helper to the center of the scene
      worldAxesRef.current = fullAxes;
      scene.add(fullAxes);

      changeCameraDirection(viewToDirection[currentView]);
    }
    if(!originRef.current)
    {
        const sphereGeom = new THREE.SphereGeometry( 1, 32, 32 );
        originRef.current = new THREE.Mesh(sphereGeom, new THREE.MeshBasicMaterial({color: 0x000000, depthTest: false, depthWrite: false, transparent: true, opacity: 0.5}));
        // scene.add(originRef.current);
    }
    return () => {
      if (worldAxesRef.current) {
        scene.remove(worldAxesRef.current);
        disposeAxes(worldAxesRef.current);
        worldAxesRef.current = null;
      }
      if(originRef.current)
      {
        // scene.remove(originRef.current);
        originRef.current.geometry.dispose();
        originRef.current.material.dispose();
        originRef.current = null;
      }
    };
  }, [scene]);

  // Effect to calculate and apply the alignment
  useEffect(() => {
    const allPointsSelected = Object.values(selections).every(s => s.completed);
    if (!shadedMesh || !allPointsSelected || isAligned) {
      return;
    }

    // --- Step 1: Get WORLD Positions of ALL Selected Points ---
    const subPatellarPos = new THREE.Vector3();
    selections.subPatellar.sphere.getWorldPosition(subPatellarPos);

    const midPatellarPos = new THREE.Vector3();
    selections.midPatellar.sphere.getWorldPosition(midPatellarPos);

    const frontEndPos = new THREE.Vector3();
    selections.frontEnd.sphere.getWorldPosition(frontEndPos);

    const internalCondylePos = new THREE.Vector3();
    selections.internalCondyle.sphere.getWorldPosition(internalCondylePos);

    const externalCondylePos = new THREE.Vector3();
    selections.externalCondyle.sphere.getWorldPosition(externalCondylePos);

    // --- Step 2: Calculate the New Origin based on specific point averages ---
    const condyleMidpoint = new THREE.Vector3().lerpVectors(internalCondylePos, externalCondylePos, 0.5);
    const verticalMidpointY = (subPatellarPos.y + frontEndPos.y) / 2;
    
    const newOrigin = new THREE.Vector3(condyleMidpoint.x, verticalMidpointY, condyleMidpoint.z);

    // --- Step 3: Calculate the New Orthonormal Basis (Coordinate System) ---

    // 3a. Primary Axis (Y - Up): The limb's longitudinal axis. This is our most important vector.
    const newY = new THREE.Vector3().subVectors(subPatellarPos, frontEndPos).normalize();

    // 3b. Forward "Intention" (Z): From the new origin to the mid-patella, projected onto the horizontal XZ plane.
    const frontIntention = new THREE.Vector3().subVectors(midPatellarPos, newOrigin);
    frontIntention.y = 0; // "ignoring y values" as requested
    frontIntention.normalize();

    // 3c. Right Axis (X): Use the cross product to find a vector perpendicular to both the limb's axis (newY)
    // and the intended front direction. This creates a stable "Right" vector.
    // We cross Y x Z_intention to get a positive X for a right-handed system.
    const newX = new THREE.Vector3().crossVectors(newY, frontIntention).normalize();

    // 3d. Final Forward Axis (Z): Recalculate Z using the cross product of our now-stable X and Y axes.
    // This creates a Z vector that is perfectly orthogonal to the other two, completing the basis.
    const newZ = new THREE.Vector3().crossVectors(newX, newY).normalize();

    // --- Step 4: Create the Transformation Matrix ---
    const alignmentMatrix = new THREE.Matrix4();
    alignmentMatrix.makeBasis(newX, newY, newZ);
    alignmentMatrix.setPosition(newOrigin);

    // --- Step 5: Apply the INVERSE Transformation to the Mesh AND the Spheres ---
    const inverseAlignmentMatrix = new THREE.Matrix4().copy(alignmentMatrix).invert();

    // Bake the transformation into the geometry
    const finalTransform = new THREE.Matrix4().multiplyMatrices(inverseAlignmentMatrix, shadedMesh.matrixWorld);
    shadedMesh.geometry.applyMatrix4(finalTransform);

    // **CRITICAL FIX**: Dispose BVH since geometry vertices have been transformed
    if (shadedMesh.geometry.boundsTree) {
      shadedMesh.geometry.disposeBoundsTree();
      shadedMesh.geometry.boundsTree = null;
    }

    // Apply the same transformation to all selection spheres so they move with the mesh
    Object.values(selections).forEach(selection => {
        if (selection.sphere) {
            selection.sphere.applyMatrix4(inverseAlignmentMatrix);
        }
    });

    // Reset the mesh's object transform to align it cleanly at the world origin
    shadedMesh.position.set(0, 0, 0);
    shadedMesh.rotation.set(0, 0, 0);
    shadedMesh.scale.set(1, 1, 1);
    shadedMesh.updateMatrixWorld(true);

    // **CRITICAL FIX**: Recompute ALL spatial data structures after geometry transformation
    // After applyMatrix4(), normals are in wrong coordinate space and must be recalculated
    shadedMesh.geometry.computeVertexNormals();  // MUST come first - fixes surface orientation
    shadedMesh.geometry.computeBoundingBox();     // Update bounds for culling
    shadedMesh.geometry.computeBoundingSphere();  // Update bounds for distance queries

    // Notify Trimline tool to recompute its default points on the newly aligned mesh.
    // We intentionally request a re-initialization so the curve starts cleanly
    // from the aligned geometry when the user opens the Trimline tool next.
    try {
      const { requestReinitialize } = useTrimlineStore.getState();
      if (typeof requestReinitialize === 'function') {
        requestReinitialize();
      }
    } catch (e) {
      // no-op; trimline tool might not be mounted yet
    }

    // **CRITICAL FIX**: Notify other tools that geometry has been modified in-place
    // This invalidates caches in tools like FlaresHandler that store geometry snapshots
    const incrementGeometryVersion = useEditorStore.getState().incrementGeometryVersion;
    incrementGeometryVersion();

    console.log("Alignment complete. Mesh and landmarks have been re-oriented.");

    // --- Step 6: Update State ---
    setAlignmentStatus(true);

  }, [shadedMesh, selections, isAligned, setAlignmentStatus]);

  return (
    <>
        <FineTuneControls />
    </>
  )
};

export default AlignmentHandler;



function createFullAxes(size, colors) {
    const group = new THREE.Group();

    const materialX = new THREE.LineBasicMaterial({ color: colors.x, opacity: 0.5, transparent: true });
    const materialY = new THREE.LineBasicMaterial({ color: colors.y, opacity: 0.5, transparent: true });
    const materialZ = new THREE.LineBasicMaterial({ color: colors.z, opacity: 0.5, transparent: true });

    const pointsX = [new THREE.Vector3(-size, 0, 0), new THREE.Vector3(size, 0, 0)];
    const pointsY = [new THREE.Vector3(0, -size, 0), new THREE.Vector3(0, size, 0)];
    const pointsZ = [new THREE.Vector3(0, 0, -size), new THREE.Vector3(0, 0, size)];

    group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pointsX), materialX));
    group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pointsY), materialY));
    group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pointsZ), materialZ));

    return group;
}

function disposeAxes(axesGroup) {
    axesGroup.traverse(obj => {
        if (obj.geometry) {
            obj.geometry.dispose();
        }
        if (obj.material) {
            if (Array.isArray(obj.material)) {
                obj.material.forEach(mat => mat.dispose());
            } else {
                obj.material.dispose();
            }
        }
    });
    if (axesGroup.parent) {
        axesGroup.parent.remove(axesGroup);
    }
}